import { useState, useRef } from 'react';
import { X, CloudLightning, ShoppingCart, Activity, AlertTriangle, Radio, Image as ImageIcon } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';

const categories = [
  { name: 'Weather', icon: CloudLightning, color: 'bg-blue-500/20 border-blue-500/50 text-blue-400 hover:bg-blue-500/30' },
  { name: 'Supply Issues', icon: ShoppingCart, color: 'bg-amber-500/20 border-amber-500/50 text-amber-400 hover:bg-amber-500/30' },
  { name: 'Health', icon: Activity, color: 'bg-emerald-500/20 border-emerald-500/50 text-emerald-400 hover:bg-emerald-500/30' },
  { name: 'Unrest', icon: AlertTriangle, color: 'bg-red-500/20 border-red-500/50 text-red-400 hover:bg-red-500/30' },
  { name: 'Other', icon: Radio, color: 'bg-violet-500/20 border-violet-500/50 text-violet-400 hover:bg-violet-500/30' },
];

export const SubmitSignalModal = ({ open, onOpenChange, onSubmit }) => {
  const [formData, setFormData] = useState({
    category: '',
    city: '',
    country: '',
    state: '',
    short_description: '',
    image: null,
    is_urgent: false
  });
  const [imagePreview, setImagePreview] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const fileInputRef = useRef(null);

  const handleCategorySelect = (categoryName) => {
    setFormData(prev => ({ ...prev, category: categoryName }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleImageUpload = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        alert('Image must be less than 5MB');
        return;
      }
      
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64 = reader.result;
        setFormData(prev => ({ ...prev, image: base64 }));
        setImagePreview(base64);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeImage = () => {
    setFormData(prev => ({ ...prev, image: null }));
    setImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.category || !formData.city || !formData.country || !formData.short_description) {
      alert('Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);
    try {
      await onSubmit(formData);
      // Reset form
      setFormData({
        category: '',
        city: '',
        country: '',
        state: '',
        short_description: '',
        image: null,
        is_urgent: false
      });
      setImagePreview(null);
      onOpenChange(false);
    } catch (error) {
      console.error('Error submitting signal:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetAndClose = () => {
    setFormData({
      category: '',
      city: '',
      country: '',
      state: '',
      short_description: '',
      image: null,
      is_urgent: false
    });
    setImagePreview(null);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={resetAndClose}>
      <DialogContent className="bg-zinc-950 border-zinc-800 max-w-md mx-4 max-h-[90vh] overflow-y-auto" data-testid="submit-signal-modal">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl font-bold text-zinc-100 tracking-tight">
            Submit Signal
          </DialogTitle>
          <DialogDescription className="text-zinc-500 text-sm">
            Report what you're seeing in your area
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Category Selection */}
          <div className="space-y-2">
            <Label className="text-zinc-300 text-sm">Category *</Label>
            <div className="grid grid-cols-2 gap-2">
              {categories.map(({ name, icon: Icon, color }) => (
                <button
                  key={name}
                  type="button"
                  onClick={() => handleCategorySelect(name)}
                  data-testid={`category-${name.toLowerCase().replace(' ', '-')}`}
                  className={`flex items-center gap-2 p-3 rounded-lg border transition-colors ${
                    formData.category === name 
                      ? color + ' border-2' 
                      : 'bg-zinc-900 border-zinc-700 text-zinc-400 hover:border-zinc-600'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span className="text-sm font-medium">{name}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Urgent Toggle */}
          <div className="flex items-center justify-between p-3 bg-zinc-900 border border-zinc-700 rounded-lg">
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-500" />
              <div>
                <Label className="text-zinc-300 text-sm font-medium">Mark as Urgent</Label>
                <p className="text-xs text-zinc-500">Urgent signals get priority notifications</p>
              </div>
            </div>
            <Switch
              checked={formData.is_urgent}
              onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_urgent: checked }))}
              data-testid="urgent-toggle"
            />
          </div>

          {/* Location Fields */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label htmlFor="city" className="text-zinc-300 text-sm">City *</Label>
              <Input
                id="city"
                name="city"
                value={formData.city}
                onChange={handleInputChange}
                placeholder="Enter city"
                className="bg-zinc-900 border-zinc-700 text-zinc-100 placeholder:text-zinc-500 focus:border-zinc-500"
                data-testid="input-city"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="country" className="text-zinc-300 text-sm">Country *</Label>
              <Input
                id="country"
                name="country"
                value={formData.country}
                onChange={handleInputChange}
                placeholder="Enter country"
                className="bg-zinc-900 border-zinc-700 text-zinc-100 placeholder:text-zinc-500 focus:border-zinc-500"
                data-testid="input-country"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="state" className="text-zinc-300 text-sm">State/Region (optional)</Label>
            <Input
              id="state"
              name="state"
              value={formData.state}
              onChange={handleInputChange}
              placeholder="Enter state or region"
              className="bg-zinc-900 border-zinc-700 text-zinc-100 placeholder:text-zinc-500 focus:border-zinc-500"
              data-testid="input-state"
            />
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="short_description" className="text-zinc-300 text-sm">Description *</Label>
            <Textarea
              id="short_description"
              name="short_description"
              value={formData.short_description}
              onChange={handleInputChange}
              placeholder="Describe what you're seeing..."
              rows={3}
              className="bg-zinc-900 border-zinc-700 text-zinc-100 placeholder:text-zinc-500 focus:border-zinc-500 resize-none"
              data-testid="input-description"
            />
          </div>

          {/* Image Upload */}
          <div className="space-y-2">
            <Label className="text-zinc-300 text-sm">Image (optional)</Label>
            {!imagePreview ? (
              <div
                onClick={() => fileInputRef.current?.click()}
                className="border-2 border-dashed border-zinc-700 rounded-lg p-6 text-center cursor-pointer hover:border-zinc-500 transition-colors"
                data-testid="image-upload-area"
              >
                <ImageIcon className="w-8 h-8 mx-auto mb-2 text-zinc-500" />
                <p className="text-sm text-zinc-400">Click to upload image</p>
                <p className="text-xs text-zinc-500 mt-1">Max 5MB</p>
              </div>
            ) : (
              <div className="relative rounded-lg overflow-hidden">
                <img src={imagePreview} alt="Preview" className="w-full h-40 object-cover" />
                <button
                  type="button"
                  onClick={removeImage}
                  className="absolute top-2 right-2 p-1 bg-zinc-900/80 rounded-full hover:bg-zinc-800 transition-colors"
                  data-testid="remove-image-btn"
                >
                  <X className="w-4 h-4 text-zinc-300" />
                </button>
              </div>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              className="hidden"
              data-testid="image-file-input"
            />
          </div>

          {/* Submit Button */}
          <Button
            type="submit"
            disabled={isSubmitting}
            className="w-full bg-red-600 hover:bg-red-700 text-white font-semibold py-3 rounded-lg transition-colors"
            data-testid="submit-signal-btn"
          >
            {isSubmitting ? 'Submitting...' : formData.is_urgent ? 'Submit Urgent Signal' : 'Submit Signal'}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
